
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileFixedCTA from '../../components/feature/MobileFixedCTA';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { createArticleStructuredData, createBreadcrumbStructuredData, createFAQStructuredData } from '../../utils/structuredData';

export default function LeveragePage() {
  // SEO設定
  usePageSEO({
    title: 'DMM FXのレバレッジ徹底解説｜最大25倍の仕組みとリスク管理',
    description: 'DMM FXのレバレッジについて徹底解説。最大25倍の仕組み、必要証拠金の計算方法、レバレッジ別の特徴、リスク管理の方法を詳しく説明します。初心者向けに安全な使い方も紹介。',
    keywords: 'DMM FX, レバレッジ, 証拠金, リスク管理, 25倍, 計算方法',
    ogImage: 'https://public.readdy.ai/ai/img_res/ecf5b694c59303bf81ccbfdf083555e7.jpg',
    structuredData: [
      createArticleStructuredData({
        headline: 'DMM FXのレバレッジ徹底解説',
        description: 'レバレッジの仕組みから設定方法、リスク管理まで詳しく解説。最大25倍のレバレッジを安全に活用する方法と、初心者向けの推奨レバレッジを紹介します。',
        image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20leverage&width=1200&height=630&seq=leverage-article&orientation=landscape',
        author: 'DMM FX情報局',
        datePublished: '2024-01-15',
        dateModified: new Date().toISOString().split('T')[0],
        url: '/leverage'
      }),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: 'レバレッジ', url: '/leverage' }
      ]),
      createFAQStructuredData([
        {
          question: 'レバレッジとは何ですか？',
          answer: 'レバレッジとは「てこの原理」を意味し、少ない資金で大きな取引ができる仕組みです。日本国内のFX取引では最大25倍のレバレッジが認められています。例えば、10万円の資金でレバレッジ25倍を使えば、250万円分の取引が可能になります。'
        },
        {
          question: 'DMM FXのレバレッジは何倍まで設定できますか？',
          answer: 'DMM FXでは、法律で定められた最大レバレッジ25倍まで利用可能です。レバレッジは取引数量に応じて自動的に調整されるため、手動で設定する必要はありません。実効レバレッジは取引数量と証拠金で決まります。'
        },
        {
          question: '初心者におすすめのレバレッジは何倍ですか？',
          answer: '初心者の方には、実効レバレッジ3-5倍程度から始めることをおすすめします。低レバレッジはリスクが低く、精神的な負担も少ないため、FXに慣れるまでは安全に取引できます。慣れてきたら徐々にレバレッジを上げていくことができます。'
        },
        {
          question: 'レバレッジのリスクは何ですか？',
          answer: 'レバレッジは利益を拡大させる一方で、損失も同様に拡大させます。高いレバレッジほど、少しの相場変動で大きな損失が発生し、ロスカットされやすくなります。対策として、証拠金維持率を200%以上に保つ、損切りラインを事前に設定する、ポジションサイズを証拠金の30%以下に抑えることが重要です。'
        },
        {
          question: '必要証拠金はどのように計算しますか？',
          answer: '必要証拠金は「取引数量 × 為替レート ÷ レバレッジ」で計算できます。例えば、1万ドル（1Lot）を1ドル=150円、レバレッジ25倍で取引する場合、10,000 × 150 ÷ 25 = 60,000円の証拠金が必要になります。'
        }
      ])
    ]
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header />
      
      {/* パンくずリスト */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'FX基礎知識', url: '/fx-basic' },
            { name: 'レバレッジ', url: '/leverage' }
          ]} />
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 text-white py-12 md:py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-5xl mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">DMM FXのレバレッジについて</h1>
            <p className="text-sm md:text-xl text-teal-50 mb-6 md:mb-8 max-w-3xl mx-auto">
              レバレッジの仕組みから設定方法、リスク管理まで徹底解説
            </p>
            <a 
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
              rel="nofollow"
              className="inline-block bg-white text-teal-600 px-6 md:px-8 py-3 md:py-4 rounded-lg text-base md:text-lg font-bold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              DMM FXで始める
            </a>
            <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          </div>
        </div>
      </section>

      {/* レバレッジとは Section */}
      <section className="py-10 md:py-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-5 md:p-8 lg:p-12">
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">レバレッジとは?</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-6 md:mb-8">
              レバレッジとは「てこの原理」を意味し、少ない資金で大きな取引ができる仕組みです。日本国内のFX取引では、<span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">最大25倍</span>のレバレッジが認められています。
            </p>
            
            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4 md:p-8 mb-6 md:mb-8">
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-3 md:mb-4">具体例で理解する</h3>
              <div className="space-y-3 md:space-y-4">
                <div className="bg-white rounded-lg p-4 md:p-6">
                  <p className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">レバレッジ1倍の場合</p>
                  <p className="text-gray-700 text-xs md:text-base">10万円の資金で、10万円分の取引が可能</p>
                </div>
                <div className="bg-white rounded-lg p-4 md:p-6">
                  <p className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">レバレッジ10倍の場合</p>
                  <p className="text-gray-700 text-xs md:text-base">10万円の資金で、100万円分の取引が可能</p>
                </div>
                <div className="bg-white rounded-lg p-4 md:p-6 border-2 border-teal-500">
                  <p className="font-semibold text-teal-900 mb-1 md:mb-2 text-sm md:text-base">レバレッジ<span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">25倍</span>の場合（最大）</p>
                  <p className="text-gray-700 text-xs md:text-base">10万円の資金で、250万円分の取引が可能</p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 md:p-6">
              <div className="flex items-start">
                <i className="ri-information-line text-xl md:text-2xl text-amber-600 mr-3 md:mr-4 flex-shrink-0 mt-0.5 md:mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">重要なポイント</h4>
                  <p className="text-gray-700 leading-relaxed text-xs md:text-base">
                    レバレッジは利益を拡大させる一方で、損失も同様に拡大させます。適切なリスク管理が必要不可欠です。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DMM FXのレバレッジ設定 Section */}
      <section className="py-10 md:py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-8 md:mb-12 text-center">DMM FXのレバレッジ設定</h2>
          
          <div className="grid md:grid-cols-2 gap-4 md:gap-8 mb-8 md:mb-12">
            <div className="bg-white rounded-xl shadow-lg p-5 md:p-8">
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-settings-3-line text-lg md:text-2xl text-white"></i>
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-3 md:mb-4">自動調整システム</h3>
              <p className="text-gray-700 leading-relaxed mb-3 md:mb-4 text-xs md:text-base">
                DMM FXでは、取引数量に応じて自動的にレバレッジが調整されます。手動でレバレッジを設定する必要はありません。
              </p>
              <div className="bg-teal-50 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm text-gray-700">
                  証拠金維持率を確認しながら、安全な範囲で取引できます
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-5 md:p-8">
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-shield-check-line text-lg md:text-2xl text-white"></i>
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-3 md:mb-4"><span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">最大25倍</span>まで対応</h3>
              <p className="text-gray-700 leading-relaxed mb-3 md:mb-4 text-xs md:text-base">
                法律で定められた最大レバレッジ25倍まで利用可能です。初心者の方は低いレバレッジから始めることをおすすめします。
              </p>
              <div className="bg-teal-50 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm text-gray-700">
                  実効レバレッジは取引数量と証拠金で決まります
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-5 md:p-8 lg:p-12">
            <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">必要証拠金の計算方法</h3>
            <p className="text-gray-700 leading-relaxed mb-4 md:mb-6 text-xs md:text-base">
              取引に必要な証拠金は、以下の計算式で求められます。
            </p>
            <div className="bg-gray-50 rounded-xl p-4 md:p-6 mb-4 md:mb-6">
              <p className="text-center text-sm md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">
                必要証拠金 = 取引数量 × 為替レート ÷ レバレッジ
              </p>
              <div className="border-t border-gray-300 pt-3 md:pt-4">
                <h4 className="font-bold text-gray-900 mb-2 md:mb-3 text-sm md:text-base">計算例</h4>
                <p className="text-gray-700 mb-1 md:mb-2 text-xs md:text-base">
                  1万ドル（1Lot）を1ドル=150円で取引する場合
                </p>
                <p className="text-gray-700 mb-3 md:mb-4 text-xs md:text-base">
                  10,000 × 150 ÷ 25 = <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded text-base md:text-xl">60,000円</span>
                </p>
                <p className="text-xs text-gray-600">
                  ※レバレッジ25倍の場合、6万円の証拠金で150万円分の取引が可能
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* レバレッジ別の特徴 Section */}
      <section className="py-10 md:py-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-8 md:mb-12 text-center">レバレッジ別の特徴</h2>
          
          <div className="space-y-4 md:space-y-6">
            <div className="bg-white rounded-xl shadow-md p-4 md:p-8">
              <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-lg md:text-2xl font-bold text-green-600">1-5倍</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-3">低レバレッジ（初心者向け）</h3>
                  <div className="grid grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
                    <div>
                      <p className="font-semibold text-green-600 mb-1 md:mb-2 text-xs md:text-base">メリット</p>
                      <ul className="text-xs md:text-sm text-gray-700 space-y-0.5 md:space-y-1">
                        <li>• リスクが低い</li>
                        <li>• 精神的な負担が少ない</li>
                        <li>• 長期保有に適している</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-red-600 mb-1 md:mb-2 text-xs md:text-base">デメリット</p>
                      <ul className="text-xs md:text-sm text-gray-700 space-y-0.5 md:space-y-1">
                        <li>• 利益が小さい</li>
                        <li>• 多くの証拠金が必要</li>
                        <li>• 資金効率が低い</li>
                      </ul>
                    </div>
                  </div>
                  <p className="text-xs md:text-sm text-gray-600">
                    おすすめ: FX初心者、リスクを抑えたい方、長期投資志向の方
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4 md:p-8">
              <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-lg md:text-2xl font-bold text-blue-600">5-15倍</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-3">中レバレッジ（中級者向け）</h3>
                  <div className="grid grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
                    <div>
                      <p className="font-semibold text-green-600 mb-1 md:mb-2 text-xs md:text-base">メリット</p>
                      <ul className="text-xs md:text-sm text-gray-700 space-y-0.5 md:space-y-1">
                        <li>• リスクと利益のバランスが良い</li>
                        <li>• 資金効率が高い</li>
                        <li>• 柔軟な取引が可能</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-red-600 mb-1 md:mb-2 text-xs md:text-base">デメリット</p>
                      <ul className="text-xs md:text-sm text-gray-700 space-y-0.5 md:space-y-1">
                        <li>• ある程度のリスクがある</li>
                        <li>• 相場の急変に注意が必要</li>
                        <li>• 証拠金管理が重要</li>
                      </ul>
                    </div>
                  </div>
                  <p className="text-xs md:text-sm text-gray-600">
                    おすすめ: FXに慣れてきた方、デイトレード・スイングトレードをする方
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4 md:p-8">
              <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-lg md:text-2xl font-bold text-red-600">15-25倍</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-3">高レバレッジ（上級者向け）</h3>
                  <div className="grid grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
                    <div>
                      <p className="font-semibold text-green-600 mb-1 md:mb-2 text-xs md:text-base">メリット</p>
                      <ul className="text-xs md:text-sm text-gray-700 space-y-0.5 md:space-y-1">
                        <li>• 少額で大きな取引が可能</li>
                        <li>• 資金効率が最大</li>
                        <li>• 短期で大きな利益を狙える</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-red-600 mb-1 md:mb-2 text-xs md:text-base">デメリット</p>
                      <ul className="text-xs md:text-sm text-gray-700 space-y-0.5 md:space-y-1">
                        <li>• リスクが非常に高い</li>
                        <li>• ロスカットされやすい</li>
                        <li>• 精神的負担が大きい</li>
                      </ul>
                    </div>
                  </div>
                  <p className="text-xs md:text-sm text-gray-600">
                    おすすめ: 経験豊富なトレーダー、スキャルピングをする方（初心者は非推奨）
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 利用者の声へのリンク */}
          <div className="mt-8 md:mt-12 bg-white rounded-xl p-4 md:p-6 border-l-4 border-teal-500 shadow-md">
            <p className="text-xs md:text-base text-gray-700 leading-relaxed">
              実際の利用者の声や評判については、こちらの記事で詳しくまとめています。<br />
              → <Link to="/reviews" className="text-teal-600 font-semibold hover:text-teal-700 underline cursor-pointer">「DMM FXの口コミ・評判は？実際どうなのか調査」</Link>
            </p>
          </div>
        </div>
      </section>

      {/* リスク管理 Section */}
      <section className="py-10 md:py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-8 md:mb-12 text-center">レバレッジ取引のリスク管理</h2>
          
          <div className="grid grid-cols-2 gap-3 md:gap-8 mb-8 md:mb-12">
            <div className="bg-white rounded-xl shadow-md p-4 md:p-8">
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-percent-line text-lg md:text-2xl text-white"></i>
              </div>
              <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-2 md:mb-4">証拠金維持率の管理</h3>
              <p className="text-gray-700 leading-relaxed mb-3 md:mb-4 text-xs md:text-base hidden md:block">
                証拠金維持率は常に確認しましょう。DMM FXでは証拠金維持率が50%を下回るとロスカットが執行されます。
              </p>
              <div className="bg-teal-50 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm font-semibold text-teal-900 mb-1 md:mb-2">推奨維持率</p>
                <p className="text-xs md:text-sm text-gray-700">
                  200%以上を維持
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4 md:p-8">
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-scissors-cut-line text-lg md:text-2xl text-white"></i>
              </div>
              <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-2 md:mb-4">損切りラインの設定</h3>
              <p className="text-gray-700 leading-relaxed mb-3 md:mb-4 text-xs md:text-base hidden md:block">
                取引前に必ず損切りラインを決めておきましょう。感情的な判断を避け、機械的に損切りすることが重要です。
              </p>
              <div className="bg-teal-50 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm font-semibold text-teal-900 mb-1 md:mb-2">目安</p>
                <p className="text-xs md:text-sm text-gray-700">
                  証拠金の2-5%程度
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4 md:p-8">
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-pie-chart-line text-lg md:text-2xl text-white"></i>
              </div>
              <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-2 md:mb-4">ポジション管理</h3>
              <p className="text-gray-700 leading-relaxed mb-3 md:mb-4 text-xs md:text-base hidden md:block">
                一度に大きなポジションを持たず、分散して取引することでリスクを軽減できます。
              </p>
              <div className="bg-teal-50 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm font-semibold text-teal-900 mb-1 md:mb-2">推奨</p>
                <p className="text-xs md:text-sm text-gray-700">
                  証拠金の30%以下
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4 md:p-8">
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-funds-line text-lg md:text-2xl text-white"></i>
              </div>
              <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-2 md:mb-4">余裕資金での取引</h3>
              <p className="text-gray-700 leading-relaxed mb-3 md:mb-4 text-xs md:text-base hidden md:block">
                生活費や必要資金ではなく、余裕資金で取引することが大前提です。
              </p>
              <div className="bg-teal-50 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm font-semibold text-teal-900 mb-1 md:mb-2">重要</p>
                <p className="text-xs md:text-sm text-gray-700">
                  失っても生活に支障がない範囲で
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-lg p-4 md:p-8">
            <div className="flex items-start">
              <i className="ri-lightbulb-line text-xl md:text-3xl text-amber-600 mr-3 md:mr-4 flex-shrink-0 mt-0.5 md:mt-1"></i>
              <div>
                <h4 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-3">初心者へのアドバイス</h4>
                <ul className="space-y-1.5 md:space-y-2 text-gray-700 text-xs md:text-base">
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">•</span>
                    <span>最初は実効レバレッジ<span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">3-5倍</span>程度から始めましょう</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">•</span>
                    <span>デモトレードで練習してから実際の取引を始めましょう</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">•</span>
                    <span>取引ルールを決めて、必ず守りましょう</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">•</span>
                    <span>慣れてきたら徐々にレバレッジを上げていきましょう</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 md:py-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-xl md:rounded-2xl shadow-xl p-6 md:p-12 text-center text-white">
            <h2 className="text-xl md:text-3xl font-bold mb-3 md:mb-4">DMM FXで安全にレバレッジ取引を始めよう</h2>
            <p className="text-sm md:text-xl text-teal-50 mb-6 md:mb-8">
              充実したサポート体制で初心者でも安心
            </p>

            {/* 口座開設手順へのリンク */}
            <div className="mb-6 md:mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-6 max-w-2xl mx-auto">
              <p className="text-white leading-relaxed text-xs md:text-base">
                口座開設の具体的な手順は、こちらで画像付きで解説しています。<br />
                → <Link to="/account-opening" className="text-teal-200 font-semibold hover:text-teal-100 underline cursor-pointer">「DMM FXの口座開設手順を画像付きで解説」</Link>
              </p>
            </div>

            <a 
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
              rel="nofollow"
              className="inline-block bg-white text-teal-600 px-8 md:px-10 py-3 md:py-4 rounded-lg text-base md:text-lg font-bold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              無料で口座開設する
            </a>
            <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          </div>
        </div>
      </section>

      {/* 関連記事 Section */}
      <section className="py-10 md:py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-8 md:mb-12 text-center">関連記事</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <Link to="/fx-basics" className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-4 md:p-6 cursor-pointer">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-book-open-line text-white text-lg md:text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">FX超入門</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4">FXの基本から学びたい方へ</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
            <Link to="/safety" className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-4 md:p-6 cursor-pointer">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-shield-check-line text-white text-lg md:text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">DMM FXは危険？安全性を徹底解説</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4">金融庁登録・信託保全など、安全性について詳しく解説</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
            <Link to="/spread" className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-4 md:p-6 cursor-pointer">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-line-chart-line text-white text-lg md:text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">スプレッドは本当に狭い？</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4">DMM FXのスプレッドと取引コストを詳しく解説</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* スマホ用固定CTAボタン */}
      <MobileFixedCTA />
      
      {/* 固定CTAの高さ分の余白（スマホのみ） */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}
